import React, { useState } from 'react';
import { X, Loader2, Wand2, Copy, FileText, Download, ThumbsUp, ExternalLink } from 'lucide-react';
import { generateTaskDescription } from '../lib/openai';
import { toast } from '../lib/toast';
import { jsPDF } from 'jspdf';
import { supabase } from '../lib/supabase';

interface SavedOutput {
  id: string;
  saved: boolean;
}

interface CustomerMoodAnalyzerProps {
  onClose: () => void;
}

export default function CustomerMoodAnalyzer({ onClose }: CustomerMoodAnalyzerProps) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [summaryGenerated, setSummaryGenerated] = useState(false);
  const [savedOutput, setSavedOutput] = useState<SavedOutput | null>(null);
  const [savingOutput, setSavingOutput] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) {
      toast.error('Please enter client feedback');
      return;
    }

    setLoading(true);
    try {
      const prompt = `Analyze the following client feedback to determine their mood and provide actionable recommendations:

Client Feedback:
${input}

Please provide a detailed and structured response in the following format:

<h2>Mood Analysis</h2>
<p><strong>Client Mood:</strong> [Determine one of the following moods: Happy and Satisfied, Neutral and Indifferent, Confused and Uncertain, Frustrated and Annoyed, Angry and Upset, Disappointed, Excited and Enthusiastic]</p>

<h3>Key Reasons:</h3>
<ul>
<li>[Reason 1 for the detected mood]</li>
<li>[Reason 2 for the detected mood]</li>
<li>[Reason 3 for the detected mood (if applicable)]</li>
</ul>

<h3>Sentiment Score</h3>
<p>The client's sentiment score is [Score between -100 (very angry) to +100 (very happy)] based on [brief explanation of score reasoning].</p>

<h3>Suggested Actions</h3>
<p>✔ [Action 1]</p>
<p><strong>Example:</strong><br>
"[Example response or message to send to client]"</p>

<p>✔ [Action 2]</p>
<p><strong>Example:</strong><br>
"[Example response or message to send to client]"</p>

<p>✔ [Action 3 (if applicable)]</p>
<p><strong>Example:</strong><br>
"[Example response or message to send to client]"</p>

<h3>Follow-up Timeline</h3>
<p>Recommended follow-up: [Immediate/Within 24 hours/Within the week/etc.]</p>

Use HTML formatting for better readability. Focus on practical, actionable recommendations that will help maintain or improve the client relationship based on their current mood.`;

      const response = await generateTaskDescription(prompt, 'task');
      // Clean up any HTML artifacts in the response
      const cleanedResponse = response.replace(/```html|```/g, '');
      setResult(cleanedResponse);
      setSummaryGenerated(true);
      toast.success('Mood analysis completed successfully');

      // Prepare for saving but don't save automatically
      setSavedOutput({
        id: crypto.randomUUID(),
        saved: false
      });
    } catch (error) {
      console.error('Error generating mood analysis:', error);
      toast.error('Failed to generate mood analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOutput = async () => {
    if (!result || !savedOutput || savedOutput.saved) return;
    
    setSavingOutput(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to save outputs');
        setSavingOutput(false);
        return;
      }
      
      // First try to find the agent by name
      const { data: existingAgent, error: findError } = await supabase
        .from('ai_agents')
        .select('id')
        .eq('name', 'Customer Mood Analyzer')
        .limit(1)
        .maybeSingle();

      if (findError && findError.code !== 'PGRST116') { // PGRST116 is "not found" error
        console.error('Error finding agent:', findError);
        toast.error('Failed to save output');
        setSavingOutput(false);
        return;
      }

      let agentId: string;

      if (!existingAgent) {
        // Create new agent with a generated UUID
        const { data: newAgent, error: createError } = await supabase
          .from('ai_agents')
          .insert({
            name: 'Customer Mood Analyzer',
            user_id: user.id,
            goals: 'Analyze customer feedback and provide mood analysis with actionable insights',
            system_prompt: 'You are an expert at analyzing customer feedback and determining customer sentiment.',
            is_custom: false,
            agent_type: 'text'
          })
          .select('id')
          .single();

        if (createError || !newAgent) {
          console.error('Error creating agent:', createError);
          toast.error('Failed to save output');
          setSavingOutput(false);
          return;
        }

        agentId = newAgent.id;
      } else {
        agentId = existingAgent.id;
      }

      // Save output to ai_agent_outputs table
      const { error: insertError } = await supabase.from('ai_agent_outputs').insert({
        user_id: user.id,
        agent_id: agentId,
        prompt: input,
        output_url: result,
        generation_type: 'text',
        llm_model: 'gpt-3.5-turbo',
        metadata: {},
        agent_type: 'text'
      });

      if (insertError) {
        console.error('Error saving output:', insertError);
        toast.error('Failed to save output');
        setSavingOutput(false);
        return;
      }
      
      toast.success('Output saved successfully');
      setSavedOutput({...savedOutput, saved: true});
    } catch (error) {
      console.error('Error saving agent output:', error);
      toast.error('Failed to save output');
    } finally {
      setSavingOutput(false);
    }
  };

  const handleCopy = async () => {
    try {
      // Create a temporary element to properly strip HTML
      const tempElement = document.createElement('div');
      tempElement.innerHTML = result || '';
      const textContent = tempElement.textContent || tempElement.innerText || '';
      
      await navigator.clipboard.writeText(textContent);
      toast.success('Copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const downloadPDF = () => {
    try {
      if (!result) return;
      
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      const margin = 20;
      
      // Add header
      pdf.setFillColor(79, 70, 229); // Indigo color
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      // Add title with white text
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      const title = 'Customer Mood Analysis';
      const titleWidth = pdf.getStringUnitWidth(title) * 22 / pdf.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      pdf.text(title, titleX, 25);
      
      // Reset text color for content
      pdf.setTextColor(0, 0, 0);
      
      // Parse HTML content
      const parser = new DOMParser();
      const doc = parser.parseFromString(result, 'text/html');
      
      // Start position after header
      let y = 50;
      
      // Add original feedback
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Client Feedback:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const wrappedInput = pdf.splitTextToSize(input, pageWidth - (margin * 2));
      pdf.text(wrappedInput, margin, y);
      y += wrappedInput.length * 7 + 15;
      
      // Process each heading and its content
      const headings = doc.querySelectorAll('h2, h3');
      headings.forEach(heading => {
        // Check if we need a new page
        if (y > pdf.internal.pageSize.height - 40) {
          pdf.addPage();
          y = 20;
        }
        
        // Add heading
        pdf.setFontSize(heading.tagName === 'H2' ? 16 : 14);
        pdf.setFont('helvetica', 'bold');
        
        // Add background for headings
        if (heading.textContent?.includes('Mood Analysis')) {
          pdf.setFillColor(240, 247, 255); // Light blue background
          pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
        }
        
        pdf.text(heading.textContent || '', margin, y);
        y += 10;
        
        // Process content until next heading
        let nextElement = heading.nextElementSibling;
        while (nextElement && !['H2', 'H3'].includes(nextElement.tagName)) {
          // Check if we need a new page
          if (y > pdf.internal.pageSize.height - 20) {
            pdf.addPage();
            y = 20;
          }
          
          if (nextElement.tagName === 'P') {
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'normal');
            
            // Check for strong tags
            const strongTags = nextElement.querySelectorAll('strong');
            if (strongTags.length > 0) {
              // Extract the strong text
              const strongText = strongTags[0].textContent || '';
              pdf.setFont('helvetica', 'bold');
              // Ensure we don't have empty text
              if (strongText.trim()) {
                pdf.text(strongText, margin, y);
              }
              pdf.setFont('helvetica', 'normal');
              
              // Get the rest of the text
              const restText = nextElement.textContent?.replace(strongText, '') || '';
              if (restText.trim()) {
                const wrappedText = pdf.splitTextToSize(restText, pageWidth - (margin * 2));
                if (wrappedText.length > 0) {
                  pdf.text(wrappedText, margin + 40, y);
                }
                y += Math.max(1, wrappedText.length) * 7;
              }
            } else if (nextElement.textContent?.includes('✔') || nextElement.textContent?.includes('✓')) {
              // Action items with checkmark
              pdf.setFont('helvetica', 'bold');
              pdf.text('√', margin, y); // Use a standard character that works in all PDF fonts
              
              const actionText = nextElement.textContent.replace(/[✔✓]/g, '').trim();
              const wrappedAction = pdf.splitTextToSize(actionText, pageWidth - (margin * 2) - 10);
              pdf.text(wrappedAction, margin + 10, y);
              y += wrappedAction.length * 7;
            } else {
              // Regular paragraph
              const wrappedText = pdf.splitTextToSize(nextElement.textContent || '', pageWidth - (margin * 2));
              pdf.text(wrappedText, margin, y);
              y += wrappedText.length * 7;
            }
          } else if (nextElement.tagName === 'UL') {
            // Process list items
            const items = nextElement.querySelectorAll('li');
            items.forEach(item => {
              // Check if we need a new page
              if (y > pdf.internal.pageSize.height - 20) {
                pdf.addPage();
                y = 20;
              }
              
              pdf.setFontSize(11);
              pdf.text('•', margin, y);
              const wrappedItem = pdf.splitTextToSize(item.textContent || '', pageWidth - (margin * 2) - 10);
              pdf.text(wrappedItem, margin + 10, y);
              y += wrappedItem.length * 7;
            });
          }
          
          nextElement = nextElement.nextElementSibling;
        }
        
        // Add some space after each section
        y += 5;
      });
      
      // Add footer with date
      pdf.setFontSize(9);
      pdf.setTextColor(128, 128, 128);
      
      // Add footer to all pages
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Generated on ${new Date().toLocaleDateString()} | Page ${i} of ${pageCount}`, margin, pdf.internal.pageSize.height - 10);
      }
      
      pdf.save('customer-mood-analysis.pdf');
    } catch (error) {
      console.error('Error creating PDF:', error);
      toast.error('Failed to create PDF');
    }
  };

  const downloadExamplePDF = () => {
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      const margin = 20;
      
      // Add header
      pdf.setFillColor(79, 70, 229); // Indigo color
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      // Add title with white text
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      const title = 'Customer Mood Analysis Example';
      const titleWidth = pdf.getStringUnitWidth(title) * 22 / pdf.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      pdf.text(title, titleX, 25);
      
      // Reset text color for content
      pdf.setTextColor(0, 0, 0);
      
      // Start position after header
      let y = 50;
      
      // Example input section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Client Feedback:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const exampleInput = "Love the new layout! It looks modern, and the navigation is so much smoother. Great job to your team!";
      const wrappedInput = pdf.splitTextToSize(exampleInput, pageWidth - (margin * 2));
      pdf.text(wrappedInput, margin, y);
      y += wrappedInput.length * 7 + 15;
      
      // Mood Analysis section
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255); // Light blue background
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Mood Analysis', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Client Mood:', margin, y);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Happy and Satisfied', margin + 40, y);
      y += 10;
      
      // Key Reasons section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Key Reasons:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const reasons = [
        'Positive response to design changes',
        'Improved user experience',
        'Appreciation for the team\'s effort'
      ];
      
      reasons.forEach(reason => {
        pdf.text('•', margin, y);
        pdf.text(reason, margin + 10, y);
        y += 7;
      });
      y += 8;
      
      // Sentiment Score section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Sentiment Score', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const sentimentText = "The client's sentiment score is +90 (Very Happy) based on their positive feedback about the new feature rollout. They appreciate the improvements and seamless user experience.";
      const wrappedSentiment = pdf.splitTextToSize(sentimentText, pageWidth - (margin * 2));
      pdf.text(wrappedSentiment, margin, y);
      y += wrappedSentiment.length * 7 + 8;
      
      // Suggested Actions section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Suggested Actions', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      const actions = [
        {
          action: 'Express Gratitude',
          example: 'Thank you so much for your kind words! We\'re thrilled that you love the new design. I\'ll pass your feedback to the team!'
        },
        {
          action: 'Ask for a Testimonial or Case Study',
          example: 'Would you be open to sharing a short testimonial about your experience? It would mean a lot!'
        }
      ];
      
      actions.forEach(item => {
        // Check if we need a new page
        if (y > pdf.internal.pageSize.height - 40) {
          pdf.addPage();
          y = 20;
        }
        
        pdf.setFont('helvetica', 'bold');
        pdf.text('✓', margin, y);
        pdf.text(item.action, margin + 10, y);
        y += 7;
        
        pdf.setFont('helvetica', 'bold');
        pdf.text('Example:', margin, y);
        y += 7;
        
        pdf.setFont('helvetica', 'normal');
        const wrappedExample = pdf.splitTextToSize(item.example, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedExample, margin + 10, y);
        y += wrappedExample.length * 7 + 5;
      });
      
      // Follow-up Timeline section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Follow-up Timeline', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Recommended follow-up: Within 24 hours', margin, y);
      
      // Add footer with date
      pdf.setFontSize(9);
      pdf.setTextColor(128, 128, 128);
      pdf.text(`Example generated by PM Pro AI`, margin, pdf.internal.pageSize.height - 10);
      
      pdf.save('customer-mood-analysis-example.pdf');
    } catch (error) {
      console.error('Error creating example PDF:', error);
      toast.error('Failed to create example PDF');
    }
  };

  const toggleFullResponse = () => {
    setShowFullResponse(!showFullResponse);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Customer Mood Analyzer
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="bg-indigo-50 dark:bg-indigo-900/50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-2">Instructions</h3>
            <p className="text-sm text-indigo-700 dark:text-indigo-300 mb-4">
              Paste client feedback or communication to analyze their mood and sentiment. The AI will determine their emotional state and provide actionable recommendations to maintain or improve the client relationship.
            </p>
            <div className="flex space-x-2">
              <button
                onClick={downloadExamplePDF}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
              >
                <Download className="h-4 w-4 mr-1.5" />
                Download Example PDF
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="client-feedback" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Client Feedback
              </label>
              <textarea
                id="client-feedback"
                rows={6}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="w-full h-24 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Paste client feedback or communication here..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5 mr-2" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-5 w-5 mr-2" />
                    Analyze Mood
                  </>
                )}
              </button>
            </div>
          </form>

          {result && (
            <div className="mt-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Analysis Results
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={handleCopy}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
                  >
                    <Copy className="h-4 w-4 mr-1.5" />
                    Copy
                  </button>
                  <button
                    onClick={downloadPDF}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <FileText className="h-4 w-4 mr-1.5" />
                    Download PDF
                  </button>
                  <button
                    onClick={handleSaveOutput}
                    disabled={savingOutput || (savedOutput && savedOutput.saved)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {savingOutput ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-1.5" />
                        Saving...
                      </>
                    ) : savedOutput && savedOutput.saved ? (
                      'Saved'
                    ) : (
                      'Save Output'
                    )}
                  </button>
                </div>
              </div>
              {summaryGenerated && (
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg mb-4">
                  <p className="text-sm text-green-700 dark:text-green-300 font-medium">
                    Mood Analysis Generated Successfully
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}